import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";

import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ActionDialog from "components/admin/ActionDialog";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import TemplatePicker from "components/admin/TemplatePicker";
import { useRunner } from "components/admin/hooks";
import { useConfirm } from "components/admin/Feedback";
import { formatNumber } from "utils/format";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";

const STATS = [
  { key: "lvl", label: "Cấp" },
  { key: "hp", label: "HP" },
  { key: "atk", label: "Công" },
  { key: "exp", label: "Kinh nghiệm (chia theo vé)" },
  { key: "str", label: "Sức" },
  { key: "agi", label: "Nhanh" },
  { key: "_int", label: "Trí" },
];

/** Theo BossTemplate.UseTicketReward: ải (1), nhiệm vụ (3), sinh nhật (6) không chia theo vé. */
const NO_TICKET_TYPES = [1, 3, 6];
const TYPE_CHALLENGE = 1;

function rewardRule(typeBoss) {
  if (!NO_TICKET_TYPES.includes(typeBoss)) {
    return {
      note: "Số lượt ở đây không dùng: top 1 sát thương rút 3 lượt, top 2 rút 2, top 3 rút 1; người trúng bốc thăm và người kết liễu rút 1 lượt",
      lines: [
        "Top 1 sát thương: rút 3 lượt + nhận đủ các quà khác (vật phẩm, tỉ lệ rơi...)",
        "Top 2 / top 3: chỉ rút 2 / 1 lượt quà ngẫu nhiên",
        "Người trúng bốc thăm (theo vé): rút 1 lượt + nhận đủ các quà khác",
        "Người kết liễu: chỉ rút 1 lượt quà ngẫu nhiên",
      ],
    };
  }
  if (typeBoss === TYPE_CHALLENGE) {
    return {
      note: "Người kết liễu rút đúng số lượt này, đồng đội rút 1 lượt",
      lines: [
        "Người kết liễu: nhận toàn bộ quà với số lượt rút như đã nhập",
        "Đồng đội trong ải: chỉ rút 1 lượt quà ngẫu nhiên",
      ],
    };
  }
  return {
    note: "Người kết liễu rút đúng số lượt này",
    lines: ["Người kết liễu nhận toàn bộ quà với số lượt rút như đã nhập"],
  };
}

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function BossEditDialog({ boss, onClose, onDone }) {
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const canEditGift = useAuth().can(PERMISSIONS.BOSS_GIFT);
  const rule = rewardRule(boss.typeBoss);
  const [form, setForm] = useState({
    name: boss.name,
    petTemplateId: boss.petTemplateId,
    lvl: boss.lvl,
    hp: boss.hp,
    atk: boss.atk,
    exp: boss.exp,
    str: boss.str,
    agi: boss.agi,
    _int: boss._int,
    gift: boss.gift || [],
    giftJoin: boss.giftJoin || [],
  });

  const changes = [
    form.name !== boss.name && `• Tên: "${boss.name}" → "${form.name}"`,
    form.petTemplateId !== boss.petTemplateId && "• Hình dạng (pet)",
    ...STATS.filter((st) => Number(form[st.key]) !== Number(boss[st.key])).map((st) => {
      const from = Number(boss[st.key]) || 0;
      const to = Number(form[st.key]) || 0;
      const pct = from > 0 ? Math.round(((to - from) * 100) / from) : null;
      return `• ${st.label}: ${formatNumber(from)} → ${formatNumber(to)}${
        pct !== null ? ` (${pct > 0 ? "+" : ""}${pct}%)` : ""
      }`;
    }),
    !same(form.gift, boss.gift || []) && "• Quà top / bốc thăm / kết liễu",
    !same(form.giftJoin, boss.giftJoin || []) && "• Quà tham gia",
  ].filter(Boolean);
  const dirty = changes.length > 0;
  // Máu hoặc công đổi quá 2 lần: dễ gõ nhầm thêm / bớt một số 0.
  const bigJump = ["hp", "atk"].some((k) => {
    const from = Number(boss[k]) || 0;
    const to = Number(form[k]) || 0;
    return from > 0 && (to >= from * 2 || to * 2 <= from);
  });

  const close = async () => {
    if (dirty) {
      const ok = await confirm({
        title: "Bỏ thay đổi?",
        message: "Bạn đã sửa boss này nhưng chưa lưu. Đóng lại thì mất các thay đổi.",
        color: "warning",
        confirmText: "Bỏ thay đổi",
      });
      if (!ok) return;
    }
    onClose();
  };

  const submit = async () => {
    const yes = await confirm({
      title: `Lưu boss ${boss.name}`,
      message: [
        ...changes,
        "",
        bigJump
          ? "⚠ Máu hoặc sức công thay đổi hơn 2 lần — kiểm tra lại có gõ thừa / thiếu số 0 không."
          : null,
        boss.alive > 0
          ? `${boss.alive} con đang có trên map giữ chỉ số và quà cũ; boss ra sau dùng bản mới.`
          : "Boss xuất hiện lần sau dùng bản mới.",
      ]
        .filter((l) => l !== null)
        .join("\n"),
      color: bigJump ? "error" : "warning",
      confirmText: "Lưu",
    });
    if (!yes) return;
    const body = {
      ...form,
      ...Object.fromEntries(STATS.map((s) => [s.key, Number(form[s.key]) || 0])),
    };
    if (!canEditGift) {
      delete body.gift;
      delete body.giftJoin;
    }
    const { ok } = await run(
      () => api.put(`/api/admin/boss/templates/${boss.bossId}`, body),
      (res) =>
        res.alive > 0
          ? `Đã lưu. ${res.alive} con đang tồn tại vẫn giữ chỉ số cũ, boss ra sau dùng chỉ số mới`
          : "Đã lưu, boss xuất hiện tiếp theo sẽ dùng chỉ số mới"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      title={`Sửa boss: ${boss.name}`}
      color="warning"
      submitText={dirty ? "Lưu…" : "Chưa có thay đổi"}
      maxWidth="lg"
      busy={busy}
      disabled={!dirty || !form.name.trim() || !form.petTemplateId}
      onClose={close}
      onSubmit={submit}
    >
      <Alert severity="info">
        Chỉ số và quà mới áp dụng ngay cho boss xuất hiện sau khi lưu. Boss đang có trên map
        {boss.alive > 0 ? ` (${boss.alive} con)` : ""} giữ nguyên chỉ số và quà cũ tới khi bị hạ
        hoặc bỏ đi.
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <MDInput
            fullWidth
            label="Tên boss (người chơi thấy)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TemplatePicker
            kind="pet"
            label="Hình dạng (pet)"
            value={form.petTemplateId}
            onChange={(id) => setForm({ ...form, petTemplateId: id })}
          />
        </Grid>
        {STATS.map((s) => (
          <Grid item xs={6} sm={4} md={3} key={s.key}>
            <MDInput
              fullWidth
              type="number"
              label={s.label}
              value={form[s.key]}
              helperText={
                Number(form[s.key]) !== Number(boss[s.key])
                  ? `Cũ: ${formatNumber(boss[s.key])}`
                  : undefined
              }
              onChange={(e) => setForm({ ...form, [s.key]: e.target.value })}
            />
          </Grid>
        ))}
      </Grid>
      <Alert severity="success" icon={<Icon>redeem</Icon>}>
        <MDTypography variant="button" fontWeight="medium" display="block">
          Cách chia quà khi hạ boss
        </MDTypography>
        {rule.lines.map((line) => (
          <MDTypography key={line} variant="caption" display="block">
            • {line}
          </MDTypography>
        ))}
        <MDTypography variant="caption" display="block" mt={0.5}>
          Chọn loại &quot;Rút ngẫu nhiên&quot; để thêm danh sách món có thể rơi (vật phẩm, nhóm vật
          phẩm hoặc điểm tích luỹ), mỗi lượt rút trúng 1 món.
        </MDTypography>
      </Alert>
      {canEditGift ? (
        <>
          <GiftEditor
            label="Quà chia cho top sát thương / bốc thăm / kết liễu"
            value={form.gift}
            randomNote={rule.note}
            onChange={(gift) => setForm((prev) => ({ ...prev, gift }))}
          />
          <GiftEditor
            label="Quà tham gia (mọi người có ít nhất 1 vé, có thể để trống)"
            value={form.giftJoin}
            onChange={(giftJoin) => setForm((prev) => ({ ...prev, giftJoin }))}
          />
        </>
      ) : (
        <>
          <Alert severity="warning">Bạn chỉ xem được quà boss, không có quyền sửa quà.</Alert>
          <MDTypography variant="button" fontWeight="medium">
            Quà chia cho top sát thương / bốc thăm / kết liễu
          </MDTypography>
          <GiftPreview lines={boss.giftInfo || []} />
          <MDTypography variant="button" fontWeight="medium">
            Quà tham gia
          </MDTypography>
          <GiftPreview lines={boss.giftJoinInfo || []} />
        </>
      )}
    </ActionDialog>
  );
}

BossEditDialog.propTypes = {
  boss: PropTypes.shape({
    bossId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    typeBoss: PropTypes.number,
    petTemplateId: PropTypes.number,
    lvl: PropTypes.number,
    hp: PropTypes.number,
    atk: PropTypes.number,
    exp: PropTypes.number,
    str: PropTypes.number,
    agi: PropTypes.number,
    _int: PropTypes.number,
    alive: PropTypes.number,
    gift: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    giftJoin: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    giftInfo: PropTypes.arrayOf(PropTypes.string),
    giftJoinInfo: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default BossEditDialog;
